/**
 * Web Push client service.
 *
 * The in-app inbox (list / mark read) is handled by the useNotifications hook via
 * Supabase directly. This service covers only Web Push: learning whether the
 * server has push configured (VAPID public key) and subscribing this device. When
 * the server reports no key (push not configured) the caller hides the enable-push
 * affordance rather than pretending push works.
 */

import { authHeader } from "@/api/authToken";

export interface PushKeyInfo { configured: boolean; publicKey: string | null }

export async function fetchPushPublicKey(): Promise<PushKeyInfo> {
  try {
    const res = await fetch(`/api/notifications/push/public-key`, {
      headers: { ...(await authHeader()) },
    });
    if (!res.ok) return { configured: false, publicKey: null };
    return (await res.json()) as PushKeyInfo;
  } catch {
    return { configured: false, publicKey: null };
  }
}

/** True only when this browser can actually do Web Push. */
export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Current push permission on this device, for the UI to reflect state. */
export function pushPermission(): NotificationPermission | "unsupported" {
  if (!pushSupported()) return "unsupported";
  return Notification.permission;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type EnablePushResult = "subscribed" | "denied" | "unsupported" | "unconfigured" | "error";

/**
 * Enable Web Push on this device: request permission, subscribe against the
 * server's VAPID key, and register the subscription. Returns a status the UI can
 * report truthfully. Never throws for an expected outcome (denied / unconfigured).
 */
export async function enablePushOnThisDevice(): Promise<EnablePushResult> {
  if (!pushSupported()) return "unsupported";

  const keyInfo = await fetchPushPublicKey();
  if (!keyInfo.configured || !keyInfo.publicKey) return "unconfigured";

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyInfo.publicKey),
      });
    }
    const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return "error";

    const res = await fetch(`/api/notifications/push/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(await authHeader()) },
      body: JSON.stringify({ subscription: json }),
    });
    return res.ok ? "subscribed" : "error";
  } catch {
    return "error";
  }
}
